import React, { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import { BankAccountRecord, listBankAccounts, listBankMovements } from '../../../../../services/BankAccountsService';
import { RowAction, RowActionsMenu } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { LedgerLine, listAccountLedger } from '../../../../../services/JournalEntriesService';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import './BankAccountTiles.css';

interface BankAccountTilesProps {
  privacyMode: boolean;
  /** Cambia al navegar por las pantallas del grupo: refresca saldos y fechas tras grabar o importar. */
  refreshKey: string;
  /** Texto de búsqueda: deja solo las pastillas que lo contienen (nombre, IBAN, moneda, subcuenta). */
  filter?: string;
  /** Opciones del menú ⋮ de cada pastilla. Sin esta función no se muestra el menú. */
  menuActions?: (account: BankAccountRecord) => RowAction[];
  /** Contenido a mostrar entre las pastillas y los apuntes (ficha o formulario de la pantalla). */
  children?: React.ReactNode;
}

interface Tile extends BankAccountRecord {
  /** Fecha del último movimiento bancario; vacía si la cuenta no tiene ninguno. */
  lastUpdate: string;
}

const formatMoney = (value: number, currency: string) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' }).format(value);

const formatDate = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
};

const LEDGER_LIMIT = 100;

/**
 * Una pastilla por cuenta bancaria, siempre visible en Tesorería y Bancos: nombre, saldo,
 * saldo contable y fecha de la última actualización (último movimiento registrado).
 * Las pastillas se desplazan en horizontal si no caben; al pulsar una se listan debajo
 * los apuntes de su subcuenta contable.
 */
export const BankAccountTiles = ({ privacyMode, refreshKey, filter = '', menuActions, children }: BankAccountTilesProps) => {
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedId, setSelectedId] = useState('');
  const [ledger, setLedger] = useState<LedgerLine[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const accounts = await listBankAccounts();
        const withDates = await Promise.all(accounts.map(async (acc): Promise<Tile> => {
          try {
            const movements = await listBankMovements(acc.id);
            const last = movements.reduce((max, m) => (m.date > max ? m.date : max), '');
            return { ...acc, lastUpdate: last };
          } catch {
            return { ...acc, lastUpdate: '' };
          }
        }));
        if (!cancelled) {
          setTiles(withDates);
          setError('');
        }
      } catch (err) {
        if (!cancelled) setError((err as Error)?.message || 'No se pudieron cargar las cuentas bancarias.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const selected = tiles.find(t => t.id === selectedId) || null;

  // Apuntes de la subcuenta de la pastilla elegida; se recargan al cambiar de pantalla (refreshKey)
  useEffect(() => {
    if (!selected || !selected.accountId) {
      setLedger([]);
      setLedgerError('');
      return;
    }
    let cancelled = false;
    setLedgerLoading(true);
    listAccountLedger(selected.accountId, LEDGER_LIMIT)
      .then(list => { if (!cancelled) { setLedger(list); setLedgerError(''); } })
      .catch(err => { if (!cancelled) { setLedger([]); setLedgerError((err as Error)?.message || 'No se pudieron cargar los apuntes.'); } })
      .finally(() => { if (!cancelled) setLedgerLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.id, selected?.accountId, refreshKey]);

  const toggle = (id: string) => setSelectedId(prev => (prev === id ? '' : id));

  if (loading) {
    return (
      <div className="bank-tiles">
        {[0, 1, 2].map(i => (
          <div key={i} className="bank-tile bank-tile--loading"><Skeleton width="100%" height="100%" /></div>
        ))}
      </div>
    );
  }

  if (error) return <div className="bank-tiles-empty">{error}</div>;
  if (tiles.length === 0) return <div className="bank-tiles-empty">No hay cuentas bancarias dadas de alta.</div>;

  const money = (v: number, currency: string) => (privacyMode ? '•••••' : formatMoney(v, currency));

  const needle = filter.trim().toLowerCase();
  const visibleTiles = needle
    ? tiles.filter(t => [t.name, t.iban, t.currency, t.accountCode, t.id].some(v => String(v || '').toLowerCase().includes(needle)))
    : tiles;

  return (
    <div className="bank-tiles-wrap">
      {visibleTiles.length === 0 && <div className="bank-tiles-empty">Ninguna cuenta coincide con la búsqueda.</div>}
      <div className="bank-tiles">
        {visibleTiles.map(tile => (
          <div
            key={tile.id}
            role="button"
            tabIndex={0}
            onClick={() => toggle(tile.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(tile.id); } }}
            className={`bank-tile ${tile.status === 'inactive' ? 'is-inactive' : ''} ${tile.id === selectedId ? 'is-selected' : ''}`}
            title={tile.name}
          >
            {menuActions && (
              <div className="bank-tile__menu" onClick={(e) => e.stopPropagation()}>
                <RowActionsMenu actions={menuActions(tile)} />
              </div>
            )}
            <div className="bank-tile__head">
              <Landmark size={14} className="bank-tile__icon" />
              <span className="bank-tile__name">{tile.name}</span>
            </div>
            <div className="bank-tile__balance-wrap">
              <span className="bank-tile__label">Saldo banco</span>
              <span className={`bank-tile__balance ${!privacyMode && tile.balance < 0 ? 'is-negative' : ''}`}>
                {money(tile.balance, tile.currency)}
              </span>
              <span
                className="bank-tile__ledger"
                title={
                  !tile.accountId
                    ? 'Esta cuenta no tiene subcuenta contable enlazada'
                    : tile.ledgerBalance === null
                      ? 'El servidor no ha devuelto el saldo contable (¿bank_accounts_list sin actualizar?)'
                      : `Saldo contable de la subcuenta ${tile.accountCode || ''}`
                }
              >
                {!tile.accountId
                  ? 'Contable: sin subcuenta'
                  : tile.ledgerBalance === null
                    ? `Contable${tile.accountCode ? ` ${tile.accountCode}` : ''}: no disponible`
                    : `Contable${tile.accountCode ? ` ${tile.accountCode}` : ''}: ${money(tile.ledgerBalance, tile.currency)}`}
              </span>
            </div>
            <div className="bank-tile__foot">
              <span className="bank-tile__label">Última actualización</span>
              <span className="bank-tile__date">{tile.lastUpdate ? formatDate(tile.lastUpdate) : 'Sin movimientos'}</span>
            </div>
          </div>
        ))}
      </div>

      {children}

      {selected && (
        <div className="bank-ledger">
          <div className="bank-ledger__head">
            <span className="bank-ledger__title">
              Movimientos de la cuenta contable{selected.accountCode ? ` ${selected.accountCode}` : ''} · {selected.name}
            </span>
            {selected.accountId && !ledgerLoading && !ledgerError && (
              <span className="bank-ledger__count">
                {ledger.length >= LEDGER_LIMIT ? `Últimos ${LEDGER_LIMIT}` : `${ledger.length} apuntes`}
              </span>
            )}
          </div>

          {!selected.accountId ? (
            <div className="bank-ledger__empty">Esta cuenta bancaria no tiene subcuenta contable enlazada: edítala para asignarle una.</div>
          ) : ledgerLoading ? (
            <div className="bank-ledger__empty">Cargando apuntes...</div>
          ) : ledgerError ? (
            <div className="bank-ledger__empty">{ledgerError}</div>
          ) : ledger.length === 0 ? (
            <div className="bank-ledger__empty">La subcuenta no tiene apuntes contabilizados.</div>
          ) : (
            <div className="bank-ledger__scroll">
              <table className="bank-ledger__table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Origen</th>
                    <th className="num">Debe</th>
                    <th className="num">Haber</th>
                    <th className="num">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((l, i) => (
                    <tr key={`${l.entryId}-${i}`}>
                      <td>{l.date ? formatDate(l.date) : '—'}</td>
                      <td>{l.concept}</td>
                      <td>{l.sourceModule || '—'}</td>
                      <td className="num">{l.debit ? money(l.debit, selected.currency) : ''}</td>
                      <td className="num">{l.credit ? money(l.credit, selected.currency) : ''}</td>
                      <td className="num">{money(l.balance, selected.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
